<script setup lang="ts">
	import { TabGroup, TabPanel, TabPanels } from '@headlessui/vue'
	import useVuelidate from '@vuelidate/core'
	import { ref } from 'vue'
	import { useToast } from 'vue-toastification'

	import ListButtonsDropdown from '@/components/ListButtonsDropdown.vue'
	import LoadingModal from '@/components/LoadingModal.vue'
	import ModalComponent from '@/components/ModalComponent.vue'
	import ShadowBox from '@/components/ShadowBox.vue'
	import StyledTabNavbar from '@/components/StyledTabNavbar.vue'

	import useAccount from '@/composables/useAccount'

	import useCompanies from '@/modules/companies/composables/useCompanies'
	import useCompanyDepartment from '@/modules/companies/composables/useCompanyDepartment'
	import useCompanyUser from '@/modules/companies/composables/useCompanyUser'
	import { CompanyEditTab } from '@/modules/companies/interfaces/companyEditTab'
	import { useCompanyGroups } from '@/modules/groups/composables/useCompanyGroups'
	import useRoles from '@/modules/roles/composables/useRoles'
	import InviteUserForm from '@/modules/users/components/InviteUserForm.vue'
	import InviteUserMassive from '@/modules/users/components/InviteUserMassive.vue'
	import UserFilters from '@/modules/users/components/UserFilters.vue'
	import UserForm from '@/modules/users/components/UserForm.vue'
	import UsersList from '@/modules/users/components/UsersList.vue'
	import useUser from '@/modules/users/composables/useUser'
	import useUsersCompany from '@/modules/users/composables/useUsersCompany'

	interface Props extends CompanyEditTab {}

	const props = defineProps<Props>()

	const toast = useToast()

	const { createForm, rulesUserValidation } = useUser({
		immediate: false,
	})

	const v$ = useVuelidate(rulesUserValidation, createForm)

	const { createUserMutationAsync } = useCompanyUser()

	const { page, pagination, data, isLoading, setPage, searchUsers } =
		useUsersCompany(
			{
				company: props.company.id,
			},
			{ immediate: true }
		)

	const {
		data: roles,
		refetch: refetchRoles,
		setCompanyId,
	} = useRoles(
		{
			companyId: props.company.id,
			immediate: true,
		},
		false
	)

	const {
		data: groups,
		setCompany,
		refetch: refetchGroups,
	} = useCompanyGroups({
		immediate: true,
		params: {
			companyId: props.company.id,
		},
	})

	const { companies } = useCompanies({})

	const {
		departments,
		refetch: refetchDepartment,
		setCompanyId: setCompanyDep,
	} = useCompanyDepartment({
		immediate: true,
		params: {
			companyId: props.company.id,
		},
	})

	const handlePage = (newPage: string) => {
		const pageNumber = Number(newPage)

		if (pageNumber >= 1 && pageNumber <= pagination.value?.meta.lastPage!) {
			setPage(newPage)
			searchUsers()
		}
	}

	const buttonsListDefault = [
		{
			name: 'Crear Usuario',
			iconName: 'person_add',
			action: () => {
				actionModalCreateUser()
			},
		},
		{
			name: 'Por Invitación',
			iconName: 'forward_to_inbox',
			action: () => {
				actionModalInviteUser()
			},
		},
	]

	const modalInviteUser = ref(false)

	function actionModalInviteUser() {
		modalInviteUser.value = !modalInviteUser.value
	}

	const modalCreateUser = ref(false)

	function actionModalCreateUser() {
		modalCreateUser.value = !modalCreateUser.value
	}

	const handleCreateUser = async () => {
		for (const field of v$.value.$silentErrors) {
			if (!Array.isArray(field.$message)) {
				toast.error(field.$message)
				return
			}
		}

		const user = await createUserMutationAsync({
			name: createForm.value.name,
			secondName: createForm.value.secondName ?? '',
			lastname: createForm.value.lastname,
			secondLastname: createForm.value.secondLastname ?? '',
			birthDate: createForm.value.birthDate,
			gender: createForm.value.gender,
			companyId: props.company.id ?? '',
			departmentId: createForm.value.departmentId ?? 0,
			roleId: createForm.value.roleId ?? '',
			email: createForm.value.email,
			companyGroupId: Number(createForm.value.companyGroupId) ?? undefined,
			jobTitle: '',
			haveAccess: createForm.value.haveAccess,
		})

		if (user) {
			toast.success('Usuario creado correctamente')

			actionModalCreateUser()

			searchUsers()
		}
	}

	const { deactivateAccountAsync, reactivateAccountAsync } = useAccount()

	const handleDeactivate = async (id: string) => {
		await deactivateAccountAsync({
			params: {
				id: Number(id),
			},
			config: {
				onSuccessToast: 'Usuario desactivado correctamente',
				raiseToast: true,
			},
		})

		searchUsers()
	}

	const handleReactivate = async (id: string) => {
		await reactivateAccountAsync({
			params: {
				id: Number(id),
			},
			config: {
				onSuccessToast: 'Usuario reactivado correctamente',
				raiseToast: true,
			},
		})

		searchUsers()
	}
</script>

<template>
	<ShadowBox class="bg-white">
		<div class="flex justify-between">
			<div>
				<h1 class="text-2xl font-bold text-gray-800">
					{{ company.name }}
				</h1>
			</div>

			<ListButtonsDropdown
				:options="buttonsListDefault"
				title="Nuevos usuarios"
			/>

			<ModalComponent
				:model-value="modalInviteUser"
				@update:model-value="actionModalInviteUser"
			>
				<template #modalBody>
					<ShadowBox class="bg-white">
						<TabGroup>
							<StyledTabNavbar
								:tabs="[
									'Invitación Individual',
									'Invitación a Varios Usuarios',
								]"
							/>

							<TabPanels>
								<!-- Invite user form -->
								<TabPanel>
									<InviteUserForm
										:companies="[]"
										:company-selected="props.company"
										@action-modal-invite-user="actionModalInviteUser"
									/>
								</TabPanel>

								<!-- Massive invitation -->
								<TabPanel>
									<InviteUserMassive
										:companies="companies"
										:company-selected="props.company"
										@action-modal-invite-user="actionModalInviteUser"
									/>
								</TabPanel>
							</TabPanels>
						</TabGroup>
					</ShadowBox>
				</template>
			</ModalComponent>

			<ModalComponent
				:model-value="modalCreateUser"
				@update:model-value="actionModalCreateUser"
			>
				<template #modalBody>
					<UserForm
						:company-selected="props.company.id"
						@send-form="handleCreateUser"
						@cancel-form="actionModalCreateUser"
					/>
				</template>
			</ModalComponent>
		</div>

		<UsersList
			:is-tab="false"
			:users="data?.data"
			:pagination="pagination"
			:page="page"
			@update:change-page="handlePage"
			@refetch="searchUsers"
			@deactivate-user="handleDeactivate"
			@reactivate-user="handleReactivate"
			:to-show-user="{
				name: 'companies.edit.users.internal',
			}"
			:to-edit-user="{
				name: 'companies.edit.users.internal',
			}"
			:to-permissions-user="{
				name: 'companies.edit.abilities.internal',
			}"
		>
			<template #filters>
				<UserFilters
					:companies="[company]"
					:roles="roles || []"
					:groups="groups || []"
					:departments="departments"
					grid-class="grid grid-cols-3 gap-4 items-center"
					:is-index="true"
				/>
			</template>
		</UsersList>
	</ShadowBox>

	<LoadingModal :when="isLoading" />
</template>
